import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../Navigation/navigation';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';

const ComprehensiveStaffWizard = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps] = useState(6);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Form data state
  const [formData, setFormData] = useState({
    // Staff Information
    staffInfo: {
      staff_id: '',
      fullname: '',
      date_of_birth: '',
      gender_id: '',
      maritalstatus_id: '',
      ethnicity_id: '',
      other_ethnicity: '',
      religion_id: '',
      father_name: '',
      mother_name: '',
      education_id: '',
      details_education: '',
      nrc_type_id: '2', // Default to 'No'
      myanmar_nrc_area_code: '',
      myanmar_nrc_township_code: '',
      myanmar_nrc_type: '',
      myanmar_nrc_number: '',
      other_id_number: '',
      other_id_description: '',
      email: '',
      phone_number: '',
      permanent_address: '',
      current_address: ''
    },
    // Job Information (array for multiple entries)
    jobInfo: [],
    // Benefits
    benefits: {
      has_regularsalary: false,
      amount: '',
      currency_id: '',
      other_currency_name: '',
      provide_accommodation: false,
      other_benefits: ''
    },
    // Skills (arrays for multiple entries) - Initialize with one empty form
    languageSkills: [{
      language_id: '',
      other_language: '',
      communicate_ids: [],
      capacity_level_id: ''
    }],
    computerSkills: [{
      computerskills_id: '',
      other_computer_skills: '',
      capacity_level_id: ''
    }],
    // Work Experience - Initialize with one empty form
    workExperience: [{
      previous_workposition: '',
      previous_organization: '',
      start_working_date: '',
      end_working_date: '',
      description: ''
    }],
    // Training - Initialize with one empty form
    training: [{
      training_id: '',
      training_location: '',
      start_date: '',
      end_date: '',
      give_certificate: false,
      organization_ids: []
    }]
  });

  // Reference data
  const [referenceData, setReferenceData] = useState({
    genders: [],
    maritalStatuses: [],
    religions: [],
    educations: [],
    ethnicities: [],
    jobPositions: [],
    organizations: [],
    nrcTypes: [],
    nrcAreas: [],
    nrcTownships: [],
    clinics: [],
    townships: [],
    districts: [],
    cadres: [],
    currencies: [],
    languages: [],
    communicationMethods: [],
    capacityLevels: [],
    computerSkills: [],
    trainings: []
  });

  // Load all reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      setLoading(true);
      try {
        const endpoints = [
          '/api/reference/genders',
          '/api/reference/marital-status',
          '/api/reference/religions',
          '/api/reference/educations',
          '/api/reference/ethnicities',
          '/api/reference/trainings',
          '/api/reference/training-statuses',
          '/api/reference/jobpositions',
          '/api/reference/organizations',
          '/api/reference/nrc-types',
          '/api/reference/nrc-areas',
          '/api/reference/nrc-townships',
          '/api/reference/clinics',
          '/api/reference/townships',
          '/api/reference/districts',
          '/api/reference/cadres',
          '/api/reference/currencies',
          '/api/reference/languages',
          '/api/reference/communication-methods',
          '/api/reference/capacity-levels',
          '/api/reference/computer-skills'
        ];

        const responses = await Promise.all(endpoints.map(endpoint => api.get(endpoint)));
        
        // Helper function to extract data from response (handles both direct arrays and wrapped responses)
        const extractData = (response) => {
          if (Array.isArray(response.data)) {
            return response.data;
          } else if (response.data && Array.isArray(response.data.data)) {
            return response.data.data;
          } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
            return response.data.data;
          }
          return [];
        };

        setReferenceData({
          genders: extractData(responses[0]),
          maritalStatuses: extractData(responses[1]),
          religions: extractData(responses[2]),
          educations: extractData(responses[3]),
          ethnicities: extractData(responses[4]),
          trainings: extractData(responses[5]),
          trainingStatuses: extractData(responses[6]),
          jobPositions: extractData(responses[7]),
          organizations: extractData(responses[8]),
          nrcTypes: extractData(responses[9]),
          nrcAreas: extractData(responses[10]),
          nrcTownships: extractData(responses[11]),
          clinics: extractData(responses[12]),
          townships: extractData(responses[13]),
          districts: extractData(responses[14]),
          cadres: extractData(responses[15]),
          currencies: extractData(responses[16]),
          languages: extractData(responses[17]),
          communicationMethods: extractData(responses[18]),
          capacityLevels: extractData(responses[19]),
          computerSkills: extractData(responses[20])
        });
      } catch (error) {
        console.error('Error loading reference data:', error);
        setErrors({ general: 'Failed to load form data. Please refresh the page.' });
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadReferenceData();
    }
  }, [isAuthenticated]);

  // Handle input changes for nested objects
  const handleInputChange = (section, field, value) => {
    // Special handling for staff_id format (Letters + 6 digits)
    if (section === 'staffInfo' && field === 'staff_id') {
      // Remove any non-alphanumeric characters and convert to uppercase
      let formattedValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      
      // Separate letters and numbers
      const letters = formattedValue.replace(/[0-9]/g, '');
      const numbers = formattedValue.replace(/[A-Z]/g, '');
      
      // Limit to max 10 letters and exactly 6 digits
      const limitedLetters = letters.substring(0, 10);
      const limitedNumbers = numbers.substring(0, 6);
      
      formattedValue = limitedLetters + limitedNumbers;
      value = formattedValue;
    }

    // Special handling for ethnicity_id changes
    if (section === 'staffInfo' && field === 'ethnicity_id') {
      // Check if we're switching FROM "Other" ethnicity TO a specific ethnicity
      const currentEthnicity = referenceData.ethnicities?.find(
        (eth) => String(eth.id) === String(formData[section].ethnicity_id)
      );
      const selectedEthnicity = referenceData.ethnicities?.find(
        (eth) => String(eth.id) === String(value)
      );
      
      const wasOtherEthnicity = currentEthnicity?.ethnicity_name?.toLowerCase().includes('other');
      const isOtherEthnicity = selectedEthnicity?.ethnicity_name?.toLowerCase().includes('other');
      
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
          // Clear other_ethnicity if switching FROM "Other" TO a specific ethnicity
          other_ethnicity: (wasOtherEthnicity && !isOtherEthnicity) ? '' : prev[section].other_ethnicity
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }

    // Clear field error when user starts typing
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: ''
      }));
    }
  };

  // Handle array data changes (skills, experience, training)
  const handleArrayDataChange = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }));
  };

  // Handle reference data updates (for adding new trainings)
  const handleReferenceDataUpdate = (section, newItem) => {
    setReferenceData(prev => ({
      ...prev,
      [section]: [...prev[section], newItem]
    }));
  };

  // Validation functions for each step
  const validateStep1 = async () => {
    const newErrors = {};
    const { staffInfo } = formData;

    // Staff ID validation (Custom format like CH000001, MTWH000001, etc.)
    if (!staffInfo.staff_id.trim()) {
      newErrors['staffInfo.staff_id'] = 'Staff ID is required';
    } else if (!/^[A-Z]{2,10}\d{6}$/.test(staffInfo.staff_id)) {
      newErrors['staffInfo.staff_id'] = 'Staff ID must be letters followed by 6 digits (e.g., CH000001, MTWH000001)';
    } else {
      // Check if staff ID already exists
      try {
        const response = await api.get(`/api/staff/check-exists/${staffInfo.staff_id}`);
        
        if (response.data.exists) {
          newErrors['staffInfo.staff_id'] = 'Staff ID already exists. Please use a different ID.';
        }
      } catch (error) {
        console.error('Error checking staff ID existence:', error);
        if (error.response?.status === 404) {
          // Endpoint not found - staff ID is available
        }
      }
    }

    // Required fields
    if (!staffInfo.fullname.trim()) newErrors['staffInfo.fullname'] = 'Full name is required';
    if (!staffInfo.date_of_birth) newErrors['staffInfo.date_of_birth'] = 'Date of birth is required';
    if (!staffInfo.gender_id) newErrors['staffInfo.gender_id'] = 'Gender is required';
    if (!staffInfo.maritalstatus_id) newErrors['staffInfo.maritalstatus_id'] = 'Marital status is required';
    if (!staffInfo.religion_id) newErrors['staffInfo.religion_id'] = 'Religion is required';
    if (!staffInfo.education_id) newErrors['staffInfo.education_id'] = 'Education is required';
    if (!staffInfo.phone_number.trim()) newErrors['staffInfo.phone_number'] = 'Phone number is required';
    if (!staffInfo.permanent_address.trim()) newErrors['staffInfo.permanent_address'] = 'Permanent address is required';
    if (!staffInfo.current_address.trim()) newErrors['staffInfo.current_address'] = 'Current address is required';

    // Email validation removed - email is now optional

    // NRC validation
    if (staffInfo.nrc_type_id === '1') {
      if (!staffInfo.myanmar_nrc_area_code) newErrors['staffInfo.myanmar_nrc_area_code'] = 'NRC area is required';
      if (!staffInfo.myanmar_nrc_township_code) newErrors['staffInfo.myanmar_nrc_township_code'] = 'NRC township is required';
      if (!staffInfo.myanmar_nrc_type) newErrors['staffInfo.myanmar_nrc_type'] = 'NRC type is required';
      if (!staffInfo.myanmar_nrc_number) newErrors['staffInfo.myanmar_nrc_number'] = 'NRC number is required';
      if (staffInfo.myanmar_nrc_number && !/^\d{6}$/.test(staffInfo.myanmar_nrc_number)) {
        newErrors['staffInfo.myanmar_nrc_number'] = 'NRC number must be 6 digits';
      }
    } else if (staffInfo.nrc_type_id === '3') {
      if (!staffInfo.other_id_number) newErrors['staffInfo.other_id_number'] = 'ID number is required';
      if (!staffInfo.other_id_description) newErrors['staffInfo.other_id_description'] = 'ID description is required';
    }

    return newErrors;
  };

  const validateStep2 = () => {
    const newErrors = {};
    const { jobInfo } = formData;

    if (!jobInfo || jobInfo.length === 0) {
      newErrors['jobInfo.general'] = 'At least one job information entry is required';
      return newErrors;
    }

    jobInfo.forEach((job, index) => {
      const selectedPosition = referenceData.jobPositions?.find(
        (position) => String(position.id) === String(job.position_id)
      );
      const isOtherPosition = selectedPosition?.position_name?.trim() === 'Other';

      if (!job.duty_location) newErrors[`jobInfo.${index}.duty_location`] = 'Duty location is required';
      if (!job.township_id) newErrors[`jobInfo.${index}.township_id`] = 'Township is required';
      if (!job.district_id) newErrors[`jobInfo.${index}.district_id`] = 'District is required';
      if (!job.position_id) newErrors[`jobInfo.${index}.position_id`] = 'Work position is required';
      if (isOtherPosition && !job.other_workposition?.trim()) {
        newErrors[`jobInfo.${index}.other_workposition`] = 'Please specify other work position';
      }
      if (!job.current_cadre) newErrors[`jobInfo.${index}.current_cadre`] = 'Cadre is required';
      if (!job.start_working_date) newErrors[`jobInfo.${index}.start_working_date`] = 'Start date is required';

      // Other cadre validation
      if (job.current_cadre === '5' && !job.other_cadre.trim()) {
        newErrors[`jobInfo.${index}.other_cadre`] = 'Please specify other cadre';
      }
    });

    return newErrors;
  };

  const validateStep3 = () => {
    const newErrors = {};
    const { benefits } = formData;

    // Salary validation
    if (benefits.has_regularsalary) {
      if (!benefits.amount.trim()) newErrors['benefits.amount'] = 'Amount is required when regular salary is provided';
      if (!benefits.currency_id) newErrors['benefits.currency_id'] = 'Currency is required when regular salary is provided';
      
      // Validate amount is a number
      if (benefits.amount && isNaN(parseFloat(benefits.amount))) {
        newErrors['benefits.amount'] = 'Amount must be a valid number';
      }

      // Other currency validation
      if (benefits.currency_id === '3' && !benefits.other_currency_name.trim()) {
        newErrors['benefits.other_currency_name'] = 'Please specify other currency';
      }
    }

    return newErrors;
  };

  // Navigate between steps
  const nextStep = async () => {
    let stepErrors = {};
    
    switch (currentStep) {
      case 1:
        stepErrors = await validateStep1();
        break;
      case 2:
        stepErrors = validateStep2();
        break;
      case 3:
        stepErrors = validateStep3();
        break;
      default:
        stepErrors = {};
    }

    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    setErrors({});
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Submit all data
  const handleSubmit = async () => {
    setSubmitting(true);
    setErrors({});

    try {
      // Filter out empty language skills
      const validLanguageSkills = formData.languageSkills.filter(skill => 
        skill.language_id && skill.language_id.trim() !== ''
      );

      // Filter out empty computer skills
      const validComputerSkills = formData.computerSkills.filter(skill => 
        skill.computerskills_id && skill.computerskills_id.trim() !== ''
      );

      // Filter out empty work experience
      const validWorkExperience = formData.workExperience.filter(exp => 
        exp.previous_workposition && exp.previous_workposition.trim() !== ''
      );

      // Filter out empty training records
      const validTraining = formData.training.filter(train => 
        train.training_id && train.training_id.trim() !== ''
      );

      // Clean up job info: if currently_working is true, ensure end_working_date is empty
      const cleanedJobInfo = formData.jobInfo.map(job => ({
        ...job,
        end_working_date: job.currently_working ? '' : job.end_working_date
      }));

      // Prepare data for submission
      const submissionData = {
        ...formData,
        staffInfo: {
          ...formData.staffInfo,
          created_by: user.user_id
        },
        jobInfo: cleanedJobInfo,
        languageSkills: validLanguageSkills,
        computerSkills: validComputerSkills,
        workExperience: validWorkExperience,
        training: validTraining
      };

      // Submit comprehensive staff data
      await api.post('/api/staff/comprehensive', submissionData);

      alert('Staff information saved successfully!');
      navigate('/staff-management');
    } catch (error) {
      console.error('Error saving staff information:', error);
      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'An error occurred while saving staff information. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return <div>Please log in to access this page.</div>;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center pt-20">
          <div className="text-lg text-gray-600">Loading form data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto px-4 pt-20 pb-8">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Add New Staff Member</h1>
          <div className="flex items-center justify-between">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i + 1}
                className={`flex flex-col items-center space-y-2 ${
                  currentStep === i + 1 
                    ? 'text-blue-600' 
                    : currentStep > i + 1 
                      ? 'text-green-600' 
                      : 'text-gray-400'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  currentStep === i + 1 
                    ? 'bg-blue-600 text-white' 
                    : currentStep > i + 1 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {i + 1}
                </div>
                <div className="text-sm font-medium">
                  {i === 0 && 'Staff Info'}
                  {i === 1 && 'Job Info'}
                  {i === 2 && 'Benefits'}
                  {i === 3 && 'Skills'}
                  {i === 4 && 'Experience'}
                  {i === 5 && 'Training'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {errors.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {errors.general}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          {currentStep === 1 && (
            <StaffInfoStep 
              formData={formData.staffInfo}
              referenceData={referenceData}
              errors={errors}
              onChange={handleInputChange}
            />
          )}
          {currentStep === 2 && (
            <JobInfoStep 
              formData={formData.jobInfo}
              referenceData={referenceData}
              errors={errors}
              onChange={handleArrayDataChange}
            />
          )}
          {currentStep === 3 && (
            <BenefitsStep 
              formData={formData.benefits}
              referenceData={referenceData}
              errors={errors}
              onChange={handleInputChange}
            />
          )}
          {currentStep === 4 && (
            <SkillsStep 
              languageSkills={formData.languageSkills}
              computerSkills={formData.computerSkills}
              referenceData={referenceData}
              onChange={handleArrayDataChange}
            />
          )}
          {currentStep === 5 && (
            <WorkExperienceStep 
              workExperience={formData.workExperience}
              onChange={handleArrayDataChange}
            />
          )}
          {currentStep === 6 && (
            <TrainingStep 
              training={formData.training}
              referenceData={referenceData}
              onChange={handleArrayDataChange}
              onReferenceDataUpdate={handleReferenceDataUpdate}
            />
          )}
        </div>

        <div className="flex items-center justify-between bg-gray-50 px-6 py-4 rounded-lg">
          <button
            type="button"
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={prevStep}
            disabled={currentStep === 1}
          >
            Previous
          </button>
          
          <div className="text-sm text-gray-600 font-medium">
            Step {currentStep} of {totalSteps}
          </div>

          {currentStep < totalSteps ? (
            <button
              type="button"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              onClick={nextStep}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Saving...' : 'Save All Information'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Step 1: Staff Information
const StaffInfoStep = ({ formData, referenceData, errors, onChange }) => {
  // Handle same address checkbox
  const handleSameAddressChange = (checked) => {
    if (checked && formData.permanent_address) {
      onChange('staffInfo', 'current_address', formData.permanent_address);
    } else if (!checked) {
      onChange('staffInfo', 'current_address', '');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Staff Information</h2>
      
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Basic Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Staff ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.staff_id}
              onChange={(e) => onChange('staffInfo', 'staff_id', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors['staffInfo.staff_id'] ? 'border-red-400 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Enter letters + 6 digits (e.g., CH000001, MTWH000001)"
              maxLength="16"
            />
            
            {/* Error messages */}
            {errors['staffInfo.staff_id'] && (
              <p className="text-red-600 text-sm mt-1">{errors['staffInfo.staff_id']}</p>
            )}
            
            <p className="text-xs text-gray-500 mt-1">Format: Letters followed by 6 digits (e.g., CH000001, MTWH000001)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.fullname}
              onChange={(e) => onChange('staffInfo', 'fullname', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.fullname'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              placeholder="Enter full name"
            />
            {errors['staffInfo.fullname'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.fullname']}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => onChange('staffInfo', 'date_of_birth', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.date_of_birth'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
            />
            {errors['staffInfo.date_of_birth'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.date_of_birth']}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.gender_id}
              onChange={(e) => onChange('staffInfo', 'gender_id', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.gender_id'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
            >
              <option value="">Select Gender</option>
              {referenceData.genders.map(gender => (
                <option key={gender.id} value={gender.id}>{gender.name}</option>
              ))}
            </select>
            {errors['staffInfo.gender_id'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.gender_id']}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Marital Status <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.maritalstatus_id}
              onChange={(e) => onChange('staffInfo', 'maritalstatus_id', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.maritalstatus_id'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
            >
              <option value="">Select Marital Status</option>
              {referenceData.maritalStatuses.map(status => (
                <option key={status.id} value={status.id}>{status.name}</option>
              ))}
            </select>
            {errors['staffInfo.maritalstatus_id'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.maritalstatus_id']}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Religion <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.religion_id}
              onChange={(e) => onChange('staffInfo', 'religion_id', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.religion_id'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
            >
              <option value="">Select Religion</option>
              {referenceData.religions.map(religion => (
                <option key={religion.id} value={religion.id}>{religion.name}</option>
              ))}
            </select>
            {errors['staffInfo.religion_id'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.religion_id']}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ethnicity
            </label>
            <select
              value={formData.ethnicity_id || ''}
              onChange={(e) => onChange('staffInfo', 'ethnicity_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select Ethnicity</option>
              {referenceData.ethnicities.map(ethnicity => (
                <option key={ethnicity.id} value={ethnicity.id}>{ethnicity.ethnicity_name}</option>
              ))}
            </select>
          </div>

          {/* Show Other Ethnicity field when needed */}
          {formData.ethnicity_id && (
            // Check if the selected ethnicity needs "other" specification
            referenceData.ethnicities.find((eth) => String(eth.id) === String(formData.ethnicity_id))?.ethnicity_name?.toLowerCase().includes('other') ||
            formData.other_ethnicity
          ) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Other Ethnicity (Please specify)
              </label>
              <input
                type="text"
                value={formData.other_ethnicity}
                onChange={(e) => onChange('staffInfo', 'other_ethnicity', e.target.value)}
                placeholder="Please specify ethnicity"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Father's Name
            </label>
            <input
              type="text"
              value={formData.father_name}
              onChange={(e) => onChange('staffInfo', 'father_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter father's name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mother's Name
            </label>
            <input
              type="text"
              value={formData.mother_name}
              onChange={(e) => onChange('staffInfo', 'mother_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter mother's name"
            />
          </div>
        </div>
      </div>

      {/* Education Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Education Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Education Level <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.education_id}
              onChange={(e) => onChange('staffInfo', 'education_id', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.education_id'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
            >
              <option value="">Select Education Level</option>
              {referenceData.educations.map(education => (
                <option key={education.id} value={education.id}>{education.name}</option>
              ))}
            </select>
            {errors['staffInfo.education_id'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.education_id']}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Education Details
            </label>
            <textarea
              value={formData.details_education}
              onChange={(e) => onChange('staffInfo', 'details_education', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter additional education details, specializations, etc."
            />
          </div>
        </div>
      </div>

      {/* National ID Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          National ID Information
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ID Type <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.nrc_type_id}
            onChange={(e) => {
              onChange('staffInfo', 'nrc_type_id', e.target.value);
              // Reset NRC fields when type changes
              if (e.target.value !== '1') {
                onChange('staffInfo', 'myanmar_nrc_area_code', '');
                onChange('staffInfo', 'myanmar_nrc_township_code', '');
                onChange('staffInfo', 'myanmar_nrc_type', '');
                onChange('staffInfo', 'myanmar_nrc_number', '');
              }
              if (e.target.value !== '3') {
                onChange('staffInfo', 'other_id_number', '');
                onChange('staffInfo', 'other_id_description', '');
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {referenceData.nrcTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>

        {/* Myanmar NRC Fields */}
        {formData.nrc_type_id === '1' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-blue-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Area Code <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.myanmar_nrc_area_code}
                onChange={(e) => onChange('staffInfo', 'myanmar_nrc_area_code', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.myanmar_nrc_area_code'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              >
                <option value="">Select Area</option>
                {referenceData.nrcAreas.map(area => (
                  <option key={area.area_code} value={area.area_code}>
                    {area.area_code} - {area.area_name}
                  </option>
                ))}
              </select>
              {errors['staffInfo.myanmar_nrc_area_code'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.myanmar_nrc_area_code']}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Township Code <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.myanmar_nrc_township_code}
                onChange={(e) => onChange('staffInfo', 'myanmar_nrc_township_code', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.myanmar_nrc_township_code'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                disabled={!formData.myanmar_nrc_area_code}
              >
                <option value="">Select Township</option>
                {referenceData.nrcTownships
                  .filter(township => township.area_code === formData.myanmar_nrc_area_code)
                  .map(township => (
                    <option key={township.township_code} value={township.township_code}>
                      {township.township_code}
                    </option>
                  ))}
              </select>
              {errors['staffInfo.myanmar_nrc_township_code'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.myanmar_nrc_township_code']}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.myanmar_nrc_type}
                onChange={(e) => onChange('staffInfo', 'myanmar_nrc_type', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.myanmar_nrc_type'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              >
                <option value="">Select Type</option>
                <option value="N">နိုင် (N)</option>
                <option value="A">ဧည့် (A)</option>
                <option value="P">ပြု (P)</option>
                <option value="F">သာ (F)</option>
                
              </select>
              {errors['staffInfo.myanmar_nrc_type'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.myanmar_nrc_type']}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.myanmar_nrc_number}
                onChange={(e) => onChange('staffInfo', 'myanmar_nrc_number', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.myanmar_nrc_number'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                placeholder="6-digit number"
                maxLength={6}
              />
              {errors['staffInfo.myanmar_nrc_number'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.myanmar_nrc_number']}</p>}
            </div>
          </div>
        )}

        {/* Other ID Fields */}
        {formData.nrc_type_id === '3' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-green-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID Type Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.other_id_description}
                onChange={(e) => onChange('staffInfo', 'other_id_description', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.other_id_description'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                placeholder="e.g., Thai ID, KNU ID, etc."
              />
              {errors['staffInfo.other_id_description'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.other_id_description']}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.other_id_number}
                onChange={(e) => onChange('staffInfo', 'other_id_number', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.other_id_number'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                placeholder="Enter ID number"
              />
              {errors['staffInfo.other_id_number'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.other_id_number']}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Contact Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => onChange('staffInfo', 'email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.email'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              placeholder="Enter email address"
            />
            {errors['staffInfo.email'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.email']}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => onChange('staffInfo', 'phone_number', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.phone_number'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              placeholder="Enter phone number"
            />
            {errors['staffInfo.phone_number'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.phone_number']}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Permanent Address <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.permanent_address}
              onChange={(e) => onChange('staffInfo', 'permanent_address', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.permanent_address'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              placeholder="Enter permanent address"
            />
            {errors['staffInfo.permanent_address'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.permanent_address']}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Address <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.current_address}
              onChange={(e) => onChange('staffInfo', 'current_address', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors['staffInfo.current_address'] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              placeholder="Enter current address"
            />
            {errors['staffInfo.current_address'] && <p className="text-red-600 text-sm mt-1">{errors['staffInfo.current_address']}</p>}
            
            <div className="mt-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.current_address === formData.permanent_address && formData.permanent_address !== ''}
                  onChange={(e) => handleSameAddressChange(e.target.checked)}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">Same as permanent address</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 2: Job Information
const JobInfoStep = ({ formData, referenceData, errors, onChange }) => {
  const isOtherPosition = (positionId) => {
    const selectedPosition = referenceData.jobPositions.find(
      (position) => String(position.id) === String(positionId)
    );
    return selectedPosition?.position_name?.trim() === 'Other';
  };

  const addJobInfo = () => {
    const newJob = {
      duty_location: '',
      township_id: '',
      district_id: '',
      appointed_by_bma: false,
      position_id: '',
      other_workposition: '',
      current_cadre: '',
      other_cadre: '',
      start_working_date: '',
      end_working_date: '',
      currently_working: false
    };
    onChange('jobInfo', [...formData, newJob]);
  };

  const updateJobInfo = (index, field, value) => {
    const updated = [...formData];
    updated[index][field] = value;
    
    // Auto-populate township and district based on clinic selection
    if (field === 'duty_location' && value) {
      const selectedClinic = referenceData.clinics.find(clinic => clinic.clinic_id === value);
      if (selectedClinic) {
        updated[index]['township_id'] = selectedClinic.township_id || '';
      }
    }
    
    onChange('jobInfo', updated);
  };

  const removeJobInfo = (index) => {
    const updated = formData.filter((_, i) => i !== index);
    onChange('jobInfo', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Job Information</h2>
        <button 
          type="button" 
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors duration-200 flex items-center gap-2" 
          onClick={addJobInfo}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Job Information
        </button>
      </div>

      {errors['jobInfo.general'] && (
        <div className="text-red-500 text-sm p-3 bg-red-100 border border-red-300 rounded-md">
          {errors['jobInfo.general']}
        </div>
      )}

      <div className="space-y-6">
        {formData.map((job, index) => (
          <div key={index} className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 transition-shadow duration-200 hover:shadow-md">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m-8 0h8" />
                </svg>
                Job Position #{index + 1}
              </h3>
              {formData.length > 1 && (
                <button
                  type="button"
                  className="px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 text-sm transition-colors duration-200 flex items-center gap-1"
                  onClick={() => removeJobInfo(index)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove
                </button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label htmlFor={`duty_location_${index}`} className="block text-sm font-medium text-gray-700">Duty Location *</label>
                <select
                  id={`duty_location_${index}`}
                  value={job.duty_location}
                  onChange={(e) => updateJobInfo(index, 'duty_location', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                >
                  <option value="">Select Clinic</option>
                  {referenceData.clinics.map((clinic) => (
                    <option key={clinic.clinic_id} value={clinic.clinic_id}>
                      {clinic.clinic_name}
                    </option>
                  ))}
                </select>
                {errors[`jobInfo.${index}.duty_location`] && (
                  <span className="text-red-500 text-sm mt-1">{errors[`jobInfo.${index}.duty_location`]}</span>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor={`township_id_${index}`} className="block text-sm font-medium text-gray-700">Township *</label>
                <select
                  id={`township_id_${index}`}
                  value={job.township_id}
                  onChange={(e) => updateJobInfo(index, 'township_id', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                >
                  <option value="">Select Township</option>
                  {referenceData.townships.map((township) => (
                    <option key={township.township_id} value={township.township_id}>
                      {township.township_name}
                    </option>
                  ))}
                </select>
                {errors[`jobInfo.${index}.township_id`] && (
                  <span className="text-red-500 text-sm mt-1">{errors[`jobInfo.${index}.township_id`]}</span>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor={`district_id_${index}`} className="block text-sm font-medium text-gray-700">District *</label>
                <select
                  id={`district_id_${index}`}
                  value={job.district_id}
                  onChange={(e) => updateJobInfo(index, 'district_id', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                >
                  <option value="">Select District</option>
                  {referenceData.districts.map((district) => (
                    <option key={district.district_id} value={district.district_id}>
                      {district.district_name} - {district.district_desc}
                    </option>
                  ))}
                </select>
                {errors[`jobInfo.${index}.district_id`] && (
                  <span className="text-red-500 text-sm mt-1">{errors[`jobInfo.${index}.district_id`]}</span>
                )}
              </div>

              <div className="space-y-2 col-span-full">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={job.appointed_by_bma}
                    onChange={(e) => updateJobInfo(index, 'appointed_by_bma', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span>Supported by BMA</span>
                </label>
              </div>

              <div className="space-y-2">
                <label htmlFor={`current_workposition_${index}`} className="block text-sm font-medium text-gray-700">Work Position *</label>
                <select
                  id={`current_workposition_${index}`}
                  value={job.position_id || ''}
                  onChange={(e) => updateJobInfo(index, 'position_id', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                >
                  <option value="">Select Position</option>
                  {referenceData.jobPositions.map((position) => (
                    <option key={position.id} value={position.id}>{position.position_name}</option>
                  ))}
                </select>
                {errors[`jobInfo.${index}.position_id`] && (
                  <span className="text-red-500 text-sm mt-1">{errors[`jobInfo.${index}.position_id`]}</span>
                )}
              </div>

              {isOtherPosition(job.position_id) && (
                <div className="space-y-2">
                  <label htmlFor={`other_workposition_${index}`} className="block text-sm font-medium text-gray-700">Other Work Position *</label>
                  <input
                    type="text"
                    id={`other_workposition_${index}`}
                    value={job.other_workposition || ''}
                    onChange={(e) => updateJobInfo(index, 'other_workposition', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                    placeholder="Please specify work position"
                  />
                  {errors[`jobInfo.${index}.other_workposition`] && (
                    <span className="text-red-500 text-sm mt-1">{errors[`jobInfo.${index}.other_workposition`]}</span>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor={`current_cadre_${index}`} className="block text-sm font-medium text-gray-700">Cadre *</label>
                <select
                  id={`current_cadre_${index}`}
                  value={job.current_cadre}
                  onChange={(e) => updateJobInfo(index, 'current_cadre', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                >
                  <option value="">Select Cadre</option>
                  {referenceData.cadres.map((cadre) => (
                    <option key={cadre.id} value={cadre.id}>
                      {cadre.name}
                    </option>
                  ))}
                </select>
                {errors[`jobInfo.${index}.current_cadre`] && (
                  <span className="text-red-500 text-sm mt-1">{errors[`jobInfo.${index}.current_cadre`]}</span>
                )}
              </div>

              {job.current_cadre === '5' && (
                <div className="space-y-2">
                  <label htmlFor={`other_cadre_${index}`} className="block text-sm font-medium text-gray-700">Other Cadre *</label>
                  <input
                    type="text"
                    id={`other_cadre_${index}`}
                    value={job.other_cadre}
                    onChange={(e) => updateJobInfo(index, 'other_cadre', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                  />
                  {errors[`jobInfo.${index}.other_cadre`] && (
                    <span className="text-red-500 text-sm mt-1">{errors[`jobInfo.${index}.other_cadre`]}</span>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor={`start_working_date_${index}`} className="block text-sm font-medium text-gray-700">Start Date *</label>
                <input
                  type="date"
                  id={`start_working_date_${index}`}
                  value={job.start_working_date}
                  onChange={(e) => updateJobInfo(index, 'start_working_date', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                />
                {errors[`jobInfo.${index}.start_working_date`] && (
                  <span className="text-red-500 text-sm mt-1">{errors[`jobInfo.${index}.start_working_date`]}</span>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor={`end_working_date_${index}`} className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  id={`end_working_date_${index}`}
                  value={job.end_working_date}
                  onChange={(e) => updateJobInfo(index, 'end_working_date', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                  disabled={job.currently_working}
                />
                <label className="flex items-center space-x-2 mt-2">
                  <input
                    type="checkbox"
                    checked={job.currently_working || false}
                    onChange={(e) => {
                      updateJobInfo(index, 'currently_working', e.target.checked);
                      if (e.target.checked) {
                        updateJobInfo(index, 'end_working_date', '');
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Currently working here</span>
                </label>
              </div>
            </div>
          </div>
        ))}

        {formData.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500">No job information added yet. Click "Add Job Information" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Step 3: Benefits
const BenefitsStep = ({ formData, referenceData, errors, onChange }) => {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">Benefits Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={formData.has_regularsalary}
              onChange={(e) => onChange('benefits', 'has_regularsalary', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span>Has Regular Salary</span>
          </label>
        </div>

        {formData.has_regularsalary && (
          <>
            <div className="space-y-2">
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Salary Amount *</label>
              <input
                type="number"
                id="amount"
                value={formData.amount}
                onChange={(e) => onChange('benefits', 'amount', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
              />
              {errors['benefits.amount'] && <span className="text-red-500 text-sm mt-1">{errors['benefits.amount']}</span>}
            </div>

            <div className="space-y-2">
              <label htmlFor="currency_id" className="block text-sm font-medium text-gray-700">Currency *</label>
              <select
                id="currency_id"
                value={formData.currency_id}
                onChange={(e) => onChange('benefits', 'currency_id', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
              >
                <option value="">Select Currency</option>
                {referenceData.currencies.map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.name}
                  </option>
                ))}
              </select>
              {errors['benefits.currency_id'] && <span className="text-red-500 text-sm mt-1">{errors['benefits.currency_id']}</span>}
            </div>

            {formData.currency_id === '3' && (
              <div className="space-y-2">
                <label htmlFor="other_currency_name" className="block text-sm font-medium text-gray-700">Other Currency *</label>
                <input
                  type="text"
                  id="other_currency_name"
                  value={formData.other_currency_name}
                  onChange={(e) => onChange('benefits', 'other_currency_name', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                />
                {errors['benefits.other_currency_name'] && <span className="text-red-500 text-sm mt-1">{errors['benefits.other_currency_name']}</span>}
              </div>
            )}
          </>
        )}

        <div className="space-y-2">
          <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={formData.provide_accommodation}
              onChange={(e) => onChange('benefits', 'provide_accommodation', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span>Provide Accommodation</span>
          </label>
        </div>

        <div className="col-span-full space-y-2">
          <label htmlFor="other_benefits" className="block text-sm font-medium text-gray-700">Other Benefits</label>
          <textarea
            id="other_benefits"
            value={formData.other_benefits}
            onChange={(e) => onChange('benefits', 'other_benefits', e.target.value)}
            rows="3"
            className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
          />
        </div>
      </div>
    </div>
  );
};

// Step 4: Skills
const SkillsStep = ({ languageSkills, computerSkills, referenceData, onChange }) => {
  const [activeTab, setActiveTab] = useState('language');

  const addLanguageSkill = () => {
    const newSkill = {
      language_id: '',
      other_language: '',
      communicate_ids: [],
      capacity_level_id: ''
    };
    onChange('languageSkills', [...languageSkills, newSkill]);
  };

  const updateLanguageSkill = (index, field, value) => {
    const updated = [...languageSkills];
    updated[index][field] = value;
    onChange('languageSkills', updated);
  };

  const removeLanguageSkill = (index) => {
    const updated = languageSkills.filter((_, i) => i !== index);
    onChange('languageSkills', updated);
  };

  const addComputerSkill = () => {
    const newSkill = {
      computerskills_id: '',
      other_computer_skills: '',
      capacity_level_id: ''
    };
    onChange('computerSkills', [...computerSkills, newSkill]);
  };

  const updateComputerSkill = (index, field, value) => {
    const updated = [...computerSkills];
    updated[index][field] = value;
    onChange('computerSkills', updated);
  };

  const removeComputerSkill = (index) => {
    const updated = computerSkills.filter((_, i) => i !== index);
    onChange('computerSkills', updated);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">Skills Information</h2>
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
          <button
            type="button"
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'language' 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => setActiveTab('language')}
          >
            Language Skills
          </button>
          <button
            type="button"
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'computer' 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => setActiveTab('computer')}
          >
            Computer Skills
          </button>
        </div>

      {activeTab === 'language' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Language Skills</h3>
            <button type="button" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50" onClick={addLanguageSkill}>
              Add Language Skill
            </button>
          </div>
          
          {languageSkills.map((skill, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-semibold text-gray-800">Language Skill #{index + 1}</h4>
                <button
                  type="button"
                  className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 text-sm"
                  onClick={() => removeLanguageSkill(index)}
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Language</label>
                  <select
                    value={skill.language_id}
                    onChange={(e) => updateLanguageSkill(index, 'language_id', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                  >
                    <option value="">Select Language</option>
                    {referenceData.languages.map((language) => (
                      <option key={language.id} value={language.id}>
                        {language.name}
                      </option>
                    ))}
                  </select>
                </div>

                {skill.language_id === '5' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Other Language</label>
                    <input
                      type="text"
                      value={skill.other_language}
                      onChange={(e) => updateLanguageSkill(index, 'other_language', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Communication Methods</label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                    {referenceData.communicationMethods.map((method) => (
                      <label key={method.id} className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={(skill.communicate_ids || []).includes(method.id)}
                          onChange={(e) => {
                            const currentIds = skill.communicate_ids || [];
                            let newIds;
                            if (e.target.checked) {
                              newIds = [...currentIds, method.id];
                            } else {
                              newIds = currentIds.filter(id => id !== method.id);
                            }
                            updateLanguageSkill(index, 'communicate_ids', newIds);
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span>{method.communicate}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Capacity Level</label>
                  <select
                    value={skill.capacity_level_id}
                    onChange={(e) => updateLanguageSkill(index, 'capacity_level_id', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                  >
                    <option value="">Select Level</option>
                    {referenceData.capacityLevels.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.level}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'computer' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Computer Skills</h3>
            <button type="button" className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50" onClick={addComputerSkill}>
              Add Computer Skill
            </button>
          </div>
          
          {computerSkills.map((skill, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-semibold text-gray-800">Computer Skill #{index + 1}</h4>
                <button
                  type="button"
                  className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 text-sm"
                  onClick={() => removeComputerSkill(index)}
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Computer Skill</label>
                  <select
                    value={skill.computerskills_id}
                    onChange={(e) => updateComputerSkill(index, 'computerskills_id', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                  >
                    <option value="">Select Skill</option>
                    {referenceData.computerSkills.map((compSkill) => (
                      <option key={compSkill.id} value={compSkill.id}>
                        {compSkill.skills}
                      </option>
                    ))}
                  </select>
                </div>

                {skill.computerskills_id === '999' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Other Computer Skill</label>
                    <input
                      type="text"
                      value={skill.other_computer_skills}
                      onChange={(e) => updateComputerSkill(index, 'other_computer_skills', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                      placeholder="Enter other computer skill"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Capacity Level</label>
                  <select
                    value={skill.capacity_level_id}
                    onChange={(e) => updateComputerSkill(index, 'capacity_level_id', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                  >
                    <option value="">Select Level</option>
                    {referenceData.capacityLevels.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.level}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

// Step 5: Work Experience
const WorkExperienceStep = ({ workExperience, onChange }) => {
  const addWorkExperience = () => {
    const newExperience = {
      previous_workposition: '',
      previous_organization: '',
      start_working_date: '',
      end_working_date: '',
      description: ''
    };
    onChange('workExperience', [...workExperience, newExperience]);
  };

  const updateWorkExperience = (index, field, value) => {
    const updated = [...workExperience];
    updated[index][field] = value;
    onChange('workExperience', updated);
  };

  const removeWorkExperience = (index) => {
    const updated = workExperience.filter((_, i) => i !== index);
    onChange('workExperience', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Previous Work Experience</h2>
        <button type="button" className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50" onClick={addWorkExperience}>
          Add Work Experience
        </button>
      </div>

      {workExperience.map((experience, index) => (
        <div key={index} className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Position</label>
              <input
                type="text"
                value={experience.previous_workposition}
                onChange={(e) => updateWorkExperience(index, 'previous_workposition', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter position title"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Organization/Clinic</label>
              <input
                type="text"
                value={experience.previous_organization}
                onChange={(e) => updateWorkExperience(index, 'previous_organization', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter organization name"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={experience.start_working_date}
                onChange={(e) => updateWorkExperience(index, 'start_working_date', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={experience.end_working_date}
                onChange={(e) => updateWorkExperience(index, 'end_working_date', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Description field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Description (Optional)</label>
            <textarea
              value={experience.description || ''}
              onChange={(e) => updateWorkExperience(index, 'description', e.target.value)}
              placeholder="Describe your responsibilities, achievements, or key learnings from this position..."
              rows={3}
              className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
            />
          </div>
          
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
              type="button"
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 text-sm font-medium transition-colors duration-200"
              onClick={() => removeWorkExperience(index)}
            >
              Remove Experience
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Step 6: Training
const TrainingStep = ({ training, referenceData, onChange, onReferenceDataUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [activeTrainingIndex, setActiveTrainingIndex] = useState(null);
  const [newTrainingName, setNewTrainingName] = useState('');
  const [newTrainingStatus, setNewTrainingStatus] = useState('2'); // Default to In-service
  const [addingTraining, setAddingTraining] = useState(false);

  // Helper function to get status name from ID
  const getStatusName = (statusId) => {
    if (referenceData.trainingStatuses) {
      const status = referenceData.trainingStatuses.find(s => s.id === parseInt(statusId));
      return status ? status.status_name : 'In-service';
    }
    // Fallback to hardcoded values if data not loaded yet
    const statusMap = {
      '1': 'Pre-service',
      '2': 'In-service', 
      '3': 'Refresher'
    };
    return statusMap[statusId] || 'In-service';
  };

  const addTraining = () => {
    const newTraining = {
      training_id: '',
      training_location: '',
      start_date: '',
      end_date: '',
      give_certificate: false,
      organization_ids: []
    };
    onChange('training', [...training, newTraining]);
  };

  const updateTraining = (index, field, value) => {
    const updated = [...training];
    updated[index][field] = value;
    onChange('training', updated);
  };

  const removeTraining = (index) => {
    const updated = training.filter((_, i) => i !== index);
    onChange('training', updated);
  };

  const handleAddNewTraining = async () => {
    if (!newTrainingName.trim()) {
      alert('Please enter a training name');
      return;
    }

    setAddingTraining(true);
    try {
      const response = await api.post('/api/trainings', {
        training_name: newTrainingName.trim(),
        training_status: parseInt(newTrainingStatus) // Convert to integer
      });

      // Handle the new enhanced response format
      const { success, training: newTrainingData } = response.data;
      
      if (success && newTrainingData) {
        // Add the new training to the reference data
        const newTraining = {
          id: newTrainingData.id,
          training_name: newTrainingData.training_name,
          training_status: newTrainingData.training_status,
          status_name: newTrainingData.status_name,
          created_at: newTrainingData.created_at
        };

        // Update reference data with new training
        onReferenceDataUpdate('trainings', newTraining);
        
        // Auto-populate the new training in the form that opened the modal
        if (activeTrainingIndex !== null) {
          updateTraining(activeTrainingIndex, 'training_id', newTrainingData.id.toString());
        }
        
        setNewTrainingName('');
        setNewTrainingStatus('2'); // Reset to default
        setShowModal(false);
        setActiveTrainingIndex(null);
        
        alert(`Training "${newTrainingData.training_name}" added successfully!`);
      } else {
        // Handle legacy response format
        const newTraining = {
          id: response.data.id,
          training_name: newTrainingName.trim(),
          training_status: parseInt(newTrainingStatus),
          status_name: getStatusName(newTrainingStatus)
        };

        onReferenceDataUpdate('trainings', newTraining);
        
        // Auto-populate the new training in the form that opened the modal
        if (activeTrainingIndex !== null) {
          updateTraining(activeTrainingIndex, 'training_id', response.data.id.toString());
        }
        
        setNewTrainingName('');
        setNewTrainingStatus('2');
        setShowModal(false);
        setActiveTrainingIndex(null);
        alert('Training added successfully!');
      }
    } catch (error) {
      console.error('Error adding training:', error);
      alert('Error adding training. Please try again.');
    } finally {
      setAddingTraining(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Training Information</h2>
        <button 
          type="button" 
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-sm" 
          onClick={addTraining}
        >
          Add Training Record
        </button>
      </div>
      
      {/* Modal for Adding New Training Type */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Add New Training</h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="modal_training_name" className="block text-sm font-medium text-gray-700">
                  Training Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="modal_training_name"
                  value={newTrainingName}
                  onChange={(e) => setNewTrainingName(e.target.value)}
                  placeholder="Enter training name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="modal_training_status" className="block text-sm font-medium text-gray-700">
                  Training Status <span className="text-red-500">*</span>
                </label>
                <select
                  id="modal_training_status"
                  value={newTrainingStatus}
                  onChange={(e) => setNewTrainingStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {referenceData.trainingStatuses && referenceData.trainingStatuses.length > 0 ? (
                    referenceData.trainingStatuses.map(status => (
                      <option key={status.id} value={status.id}>
                        {status.status_name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="1">Pre-service</option>
                      <option value="2">In-service</option>
                      <option value="3">Refresher</option>
                    </>
                  )}
                </select>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleAddNewTraining}
                  disabled={addingTraining || !newTrainingName.trim()}
                >
                  {addingTraining ? 'Adding...' : 'Add Training'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {training.map((trainingItem, index) => (
        <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Training #{index + 1}</h3>
            <button
              type="button"
              className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 text-sm"
              onClick={() => removeTraining(index)}
            >
              Remove
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Training</label>
              <select
                value={trainingItem.training_id}
                onChange={(e) => {
                  if (e.target.value === 'add_new') {
                    setActiveTrainingIndex(index);
                    setShowModal(true);
                  } else {
                    updateTraining(index, 'training_id', e.target.value);
                  }
                }}
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
              >
                <option value="">Select Training</option>
                {referenceData.trainings.map((train) => (
                  <option key={train.id} value={train.id}>
                    {train.training_name} ({train.status_name || getStatusName(train.training_status)})
                  </option>
                ))}
                <option value="add_new" className="font-semibold text-blue-600">
                  + Add New Training
                </option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <input
                type="text"
                value={trainingItem.training_location}
                onChange={(e) => updateTraining(index, 'training_location', e.target.value)}
                placeholder="Training location..."
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={trainingItem.start_date}
                onChange={(e) => updateTraining(index, 'start_date', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={trainingItem.end_date}
                onChange={(e) => updateTraining(index, 'end_date', e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={trainingItem.give_certificate}
                  onChange={(e) => updateTraining(index, 'give_certificate', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span>Certificate Given</span>
              </label>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Certifying Organizations</label>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                {referenceData.organizations.map(org => (
                  <label key={org.id} className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(trainingItem.organization_ids || []).includes(org.id)}
                      onChange={(e) => {
                        const currentIds = trainingItem.organization_ids || [];
                        let newIds;
                        if (e.target.checked) {
                          newIds = [...currentIds, org.id];
                        } else {
                          newIds = currentIds.filter(id => id !== org.id);
                        }
                        updateTraining(index, 'organization_ids', newIds);
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span>{org.organization_name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ComprehensiveStaffWizard;
